import classNames from 'classnames';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { ref, onValue, get } from 'firebase/database';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { createPages, languages } from './utils';
import Logo from '../../assets/img/logo.png';
import { subjectsObjKey } from '../../Common/Database';
import { database, auththentication } from '../../Common/Firebase';
import { setActiveLangSl } from '../../redux/slices/backHistorySlice';
import { setLangType, toggleNotificationFlagUser } from '../../redux/slices/userSlice';
import HistoryButton from '../HistoryButton';
import Notification from '../Notification';

import './style.scss';

const Header = ({ user, userType, userMail, authLoading, isAuthenticated, onLogout }) => {
  const locationName = useLocation().pathname;
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [navOpen, setNavOpen] = useState(false);
  const [openMenuProfile, setOpenMenuProfile] = useState(false);
  const [anchorElLang, setAnchorElLang] = useState(false);
  const [activeLang, setActiveLang] = useState(0);
  const arrSubjects =
    userType === 'Student'
      ? Object.values(user?.subjects || {})
          .filter(item => item?.activeSub)
          .map(item => item.name)
      : [];
  const [openNotif, setOpenNotif] = useState(false);
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [notificationFlag, setNotificationFlag] = useState(false);
  const [viewedCount, setViewedCount] = useState(0);
  const [loginError, setLoginError] = useState(null);

  const langMenuRef = useRef();
  const langMenuItemRef = useRef();
  const profileMenuRef = useRef();
  const profileAvatarRef = useRef();
  const navigationMenuRef = useRef();
  const burgerButtonRef = useRef();
  const notificationRef = useRef();
  const notificationBtnRef = useRef();

  const pages = createPages(userMail, arrSubjects);
  const menuPages = pages[userType === 'Admin' ? 'NewAdmin' : userType] || [];

  useEffect(() => {
    const handler = e => {
      if (
        langMenuRef.current?.contains(e.target) ||
        langMenuItemRef.current?.contains(e.target) ||
        profileMenuRef.current?.contains(e.target) ||
        profileAvatarRef.current?.contains(e.target) ||
        navigationMenuRef.current?.contains(e.target) ||
        burgerButtonRef.current?.contains(e.target) ||
        notificationRef.current?.contains(e.target) ||
        notificationBtnRef.current?.contains(e.target)
      ) {
        // Если один из элементов был нажат, не делайте ничего
        return;
      }

      // Иначе, закрыть все меню
      setAnchorElLang(false);
      setOpenMenuProfile(false);
      setNavOpen(false);
      setOpenNotif(false);
    };

    document.addEventListener('mousedown', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
    };
  });

  useEffect(() => {
    setNavOpen(false);
    setOpenMenuProfile(false);
    setOpenNotif(false);
    setStudents([]);
    setLoginError(null);
  }, [user, userType]);

  // Students are needed for notification details, never to render the menu.
  useEffect(() => {
    if (!openNotif || !userMail) return;
    let cancelled = false;
    get(ref(database, 'Students'))
      .then(snapshot => {
        if (!cancelled) setStudents(Object.values(snapshot.val() || {}).filter(item => item?.type === 'Student'));
      })
      .catch(() => {
        if (!cancelled) setStudents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [openNotif, userMail, userType]);

  const signIn = () => {
    setLoginError(null);
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    signInWithPopup(auththentication, provider)
      .then(() => {
        navigate('/');
      })
      .catch(() => {
        setLoginError('Не вдалося увійти. Спробуйте ще раз.');
      });
  };

  const signOut = event => {
    event.preventDefault();
    onLogout();
  };

  const handleLanguageChange = langIndex => {
    i18n.changeLanguage(languages[langIndex].lang);
    setActiveLang(langIndex);
    setAnchorElLang(false);
    localStorage.setItem('selectedLanguage', langIndex);
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage !== null) {
      const parsed = Number(savedLanguage);
      const langIndex = Number.isInteger(parsed) && languages[parsed] ? parsed : 0;
      setActiveLang(langIndex);
      dispatch(setActiveLangSl(languages[langIndex].lang));
      i18n.changeLanguage(languages[langIndex].lang);
    }
  }, [dispatch, i18n]);

  useEffect(() => {
    setItems([]);
    setViewedCount(0);
    setNotificationFlag(false);
    if (!userMail || !userType) return;
    let cancelled = false;
    const dbRef =
      userType === 'Student'
        ? ref(database, `Students/${userMail}/notification`)
        : ref(database, `Teachers/${userMail}/notification`);

    const unsubscribe = onValue(
      dbRef,
      snapshot => {
        if (cancelled) return;
        const obj = snapshot.val();
        if (obj) {
          const arr = Object.keys(obj).map(key => obj[key]);

          // Считаем количество элементов с viewed=true
          const trueViewedCount = arr.filter(item => item.viewed === true).length;

          // Устанавливаем состояние notificationFlag
          if (trueViewedCount > 0) {
            setNotificationFlag(true);
          } else {
            setNotificationFlag(false);
          }

          // Устанавливаем состояние viewedCount
          setViewedCount(trueViewedCount);
          setItems(arr);
          dispatch(toggleNotificationFlagUser());
        } else {
          setNotificationFlag(false);
          setItems([]);
          setViewedCount(0); // Устанавливаем значение обратно в 0, если нет данных
        }
      },
      () => {
        if (!cancelled) {
          setItems([]);
          setViewedCount(0);
          setNotificationFlag(false);
        }
      },
    );

    // Функция очистки, которая будет вызвана при размонтировании компонента
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userMail, userType, dispatch]);

  useEffect(() => {
    dispatch(setActiveLangSl(languages[activeLang].lang));
  }, [activeLang, dispatch]);

  const handleNotification = () => {
    setOpenNotif(prev => !prev);
  };

  const CloseNotificationFunc = () => {
    setOpenNotif(false);
  };

  const handleActionLang = i => {
    handleLanguageChange(i);
    dispatch(setLangType(i));
  };

  return (
    <header className="header">
      {loginError && <div role="alert">{loginError}</div>}
      <div className="header-container container">
        <>
          <HistoryButton user={user} />

          <Link to="/" className="logo">
            <img src={Logo} alt="logo" />
          </Link>

          {user ? (
            <>
              <nav className="navigation">
                <div
                  className={classNames('navigation-items', {
                    open: navOpen,
                  })}
                  ref={navigationMenuRef}
                >
                  {menuPages.map(page => {
                    switch (page.type) {
                      case 'link':
                        return (
                          <Link
                            key={page.name}
                            className={locationName == page.route ? 'active' : ''}
                            to={page.route}
                            onClick={() => setNavOpen(false)}
                          >
                            <span className="text">{t(page.name)}</span>
                          </Link>
                        );
                      case 'dropdown homework-teacher':
                        return (
                          <span className={locationName == page.route ? 'dropdown active' : 'dropdown'} key={page.name}>
                            <span className="text">
                              {t(page.name)}
                              {<span className="icon-chevron-down"></span>}
                            </span>

                            {
                              <ul className={'dropdown-list'}>
                                {page.dropList &&
                                  page.dropList.map((item, index) => {
                                    return (
                                      <li key={index}>
                                        <Link to={`/${item.link}`} onClick={() => setNavOpen(false)}>
                                          {t(item.name)}
                                        </Link>
                                      </li>
                                    );
                                  })}
                              </ul>
                            }
                          </span>
                        );
                      case 'dropdown homework':
                        return (
                          <span className={locationName == page.route ? 'dropdown active' : 'dropdown'} key={page.name}>
                            <span className="text">
                              {t(page.name)}
                              {arrSubjects.length > 0 && <span className="icon-chevron-down"></span>}
                            </span>

                            {arrSubjects.length > 0 && (
                              <ul className={arrSubjects.length > 0 ? 'dropdown-list' : 'dropdown-list hidden'}>
                                {page.dropList &&
                                  page.dropList
                                    .map((item, i) => {
                                      const translatedItem = t(subjectsObjKey[item]);
                                      return {
                                        index: i,
                                        value: translatedItem,
                                      };
                                    })
                                    .sort((a, b) => a.value.localeCompare(b.value))
                                    .map(item => {
                                      switch (page.name) {
                                        case 'homework':
                                          return (
                                            <li key={uuidv4()}>
                                              <Link
                                                to={`/student-homework-list/${page.dropList[item.index]}`}
                                                onClick={() => setNavOpen(false)}
                                              >
                                                {t(item.value)}
                                              </Link>
                                            </li>
                                          );

                                        case 'subjects':
                                          return (
                                            <li key={uuidv4()}>
                                              <Link
                                                to={`/subject/${userMail}/${page.dropList[item.index]}`}
                                                onClick={() => setNavOpen(false)}
                                              >
                                                {t(item.value)}
                                              </Link>
                                            </li>
                                          );
                                      }
                                    })}
                              </ul>
                            )}
                          </span>
                        );

                      case 'dropdown students-teacher':
                        return (
                          <span className={locationName == page.route ? 'dropdown active' : 'dropdown'} key={page.name}>
                            <span className="text">
                              {t(page.name)}
                              {<span className="icon-chevron-down"></span>}
                            </span>

                            {
                              <ul className={'dropdown-list'}>
                                {page.dropList &&
                                  page.dropList.map((item, index) => {
                                    return (
                                      <li key={index}>
                                        <Link to={`/${item.link}`} onClick={() => setNavOpen(false)}>
                                          {t(item.name)}
                                        </Link>
                                      </li>
                                    );
                                  })}
                              </ul>
                            }
                          </span>
                        );

                      case 'dropdown subjects':
                        return (
                          <span className={locationName == page.route ? 'dropdown active' : 'dropdown'} key={page.name}>
                            <span className="text">
                              {t(page.name)}
                              {arrSubjects.length > 0 && <span className="icon-chevron-down"></span>}
                            </span>

                            {arrSubjects.length > 0 && (
                              <ul className={arrSubjects.length > 0 ? 'dropdown-list' : 'dropdown-list hidden'}>
                                {page.dropList &&
                                  page.dropList
                                    .map((item, i) => {
                                      const translatedItem = t(subjectsObjKey[item]);
                                      return {
                                        index: i,
                                        value: translatedItem,
                                      };
                                    })
                                    .sort((a, b) => a.value.localeCompare(b.value))
                                    .map(item => {
                                      switch (page.name) {
                                        case 'homework':
                                          return (
                                            <li key={uuidv4()}>
                                              <Link
                                                to={`/student-homework-list/${page.dropList[item.index]}`}
                                                onClick={() => setNavOpen(false)}
                                              >
                                                {t(item.value)}
                                              </Link>
                                            </li>
                                          );

                                        case 'subjects':
                                          return (
                                            <li key={uuidv4()}>
                                              <Link
                                                to={`/subject/${userMail}/${page.dropList[item.index]}`}
                                                onClick={() => setNavOpen(false)}
                                              >
                                                {t(item.value)}
                                              </Link>
                                            </li>
                                          );
                                      }
                                    })}
                              </ul>
                            )}
                          </span>
                        );
                      case 'dropdown checkLists':
                        return (
                          <span className={locationName == page.route ? 'dropdown active' : 'dropdown'} key={page.name}>
                            <span className="text">
                              {t(page.name)}
                              {<span className="icon-chevron-down"></span>}
                            </span>

                            {
                              <ul className={'dropdown-list'}>
                                {page.dropList &&
                                  page.dropList.map((item, index) => {
                                    return (
                                      <li key={index}>
                                        <Link to={`/${item.link}`} onClick={() => setNavOpen(false)}>
                                          {t(item.name)}
                                        </Link>
                                      </li>
                                    );
                                  })}
                              </ul>
                            }
                          </span>
                        );
                    }
                  })}
                </div>
              </nav>

              <div className="header-menu-left">
                <div className="notif-wrap">
                  <button
                    className={classNames('bell-button', {
                      active: openNotif,
                      activeFlag: notificationFlag,
                    })}
                    onClick={handleNotification}
                    ref={notificationBtnRef}
                  >
                    <span className="icon icon-bell"></span>
                  </button>

                  {viewedCount > 0 && (
                    <span className="notif-wrap-count">
                      <span>{viewedCount < 99 ? viewedCount : '99+'}</span>
                    </span>
                  )}

                  {openNotif && (
                    <Notification
                      notificationRef={notificationRef}
                      openNotif={openNotif}
                      students={students}
                      items={items}
                      closeFunc={CloseNotificationFunc}
                      userMail={userMail}
                      userType={userType}
                      t={t}
                    />
                  )}
                </div>

                <div className="lang-wrap">
                  <a
                    className={anchorElLang ? 'lang-btn open' : 'lang-btn close'}
                    onClick={() => {
                      setAnchorElLang(prev => !prev);
                    }}
                    ref={langMenuRef}
                  >
                    {i18n.language.toUpperCase()}
                    <span className="icon-chevron-down"></span>
                  </a>
                  <div className={anchorElLang ? 'drop-down-lang open' : 'drop-down-lang'} ref={langMenuItemRef}>
                    {languages.map((lang, i) => (
                      <div
                        key={lang.lang}
                        className={i === activeLang ? 'menu-item active' : 'menu-item'}
                        onClick={() => handleActionLang(i)}
                      >
                        <span>{lang.lang.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {user?.photoURL && (
                  <a className="avatar-button" onClick={() => setOpenMenuProfile(prev => !prev)} ref={profileAvatarRef}>
                    <img src={user?.photoURL} alt={'avatar'} className="avatar-image" referrerPolicy="no-referrer" />
                  </a>
                )}

                <button
                  className="burger-button"
                  type="button"
                  onClick={() => setNavOpen(prev => !prev)}
                  ref={burgerButtonRef}
                >
                  {navOpen ? (
                    <span className="burger-item burger-item-close"></span>
                  ) : (
                    <span className="burger-item burger-item-start"></span>
                  )}
                </button>

                <div className={openMenuProfile ? 'menu-profile open' : 'menu-profile'} ref={profileMenuRef}>
                  {/* {userType === 'Student' && (
                    <div className="menu-item" key="Profile">
                      <Link to={`/profile`}>
                        <span className="text">{t('profile')}</span>
                        <span className="icon icon-user"></span>
                      </Link>
                    </div>
                  )} */}

                  <div className="menu-item" key="Sign out">
                    <a href="#" onClick={signOut}>
                      <span className="text">{t('logout')}</span>
                      <span className="icon icon-logout"></span>
                    </a>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <button
                className="btn more btn-view"
                disabled={authLoading}
                onClick={isAuthenticated ? onLogout : signIn}
              >
                <span>{t(isAuthenticated ? 'logout' : 'login')}</span>
              </button>
            </>
          )}
        </>
      </div>
    </header>
  );
};

Header.propTypes = {
  user: PropTypes.shape({ subjects: PropTypes.object, photoURL: PropTypes.string }),
  userType: PropTypes.string,
  userMail: PropTypes.string,
  authLoading: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  onLogout: PropTypes.func.isRequired,
};

export default memo(Header);
